/**
 * ONE click-to-call flow for every Call button — Card P91c item 2.
 *
 * ⛔⛔ WHY THIS EXISTS. There were THREE copies of this flow
 * (ContactDetailView, ContactsTab, CallSurfaceTab) and they had already
 * drifted: P91 reworded the server's "no callback number" message, and the two
 * copies that matched the error PROSE (`/agent phone/i`) silently lost their
 * "save your number and retry" recovery — the rep hit a dead end exactly where
 * the form used to fix it for them. One copy was updated, two were not.
 *
 * ⚠ A `code` is a contract; a sentence is copy, and copy gets edited. This
 * branches on `r.code` only.
 *
 * ⛔ AND THE PROMPT IS NOT A CONVENIENCE — IT IS THE ONLY WAY. There is no
 * profile screen in this app: `users.phone` is written by exactly one channel
 * (`user:profile:update-self`), and the only callers are these buttons.
 * SettingsPage has `general` and `security` tabs and nothing else. So telling
 * a rep to "open Settings → My profile" sends them somewhere that does not
 * exist. The prompt below IS the place, and the copy says so.
 */

/** Error codes that mean "we need a callback number from this rep". */
export const NEEDS_CALLBACK_NUMBER = ['NO_AGENT_PHONE', 'AGENT_PHONE_NOT_E164'];

/**
 * What a rep is told when an earlier request is still unresolved.
 *
 * ⚠ Deliberately different from ALREADY_PLACING: nothing is being placed, and
 * saying so would be a lie the rep can disprove by looking at their phone.
 * This names the real situation and gives them the one check that settles it.
 */
export const CALL_REQUEST_PENDING =
  'A previous call request is still pending — check your phone before trying again.';

/** What a rep is told when a call is already being placed. */
export const ALREADY_PLACING = 'A call is already being placed — wait for that one to connect.';

/*
 * ⛔⛔ ONE CALL AT A TIME PER REP, AND THE GUARD LIVES HERE — Card P91f item 1.
 *
 * ⚠ THE POINT OF THIS FEATURE IS THAT TWILIO RINGS THE REP FIRST. So a second
 * call placed while the first is still being set up rings the SAME HUMAN twice
 * — two legs, one person, and whichever contact answers first is connected to
 * a confused silence. The guard is therefore about the REP, not the button.
 *
 * ⛔ P91e's guard was ROW-LOCAL (a `calling` state inside ContactRow), which is
 * the wrong scope in three measured ways:
 *   • click Call on contact A, then on contact B while A is still being placed
 *     — two rows, two flags, two rings;
 *   • collapsing and reopening a letter group UNMOUNTS the row, resetting the
 *     flag mid-flight, so even the SAME row can be clicked again;
 *   • ContactDetailView and CallSurfaceTab each hold their own flag and cannot
 *     see the others at all.
 *
 * ⚠ MODULE SCOPE IS THE RIGHT SCOPE — not a React ref, not context. The thing
 * being protected is the rep's one telephone, and there is one rep per window.
 * Every surface imports this module, so every surface shares the guard.
 *
 * ⚠ The per-button `disabled` state STAYS. That is what the rep SEES; this is
 * what the system ENFORCES. They answer different questions, and the row-local
 * one is still the right answer to its own.
 */
let callInFlight = false;

/*
 * ⛔⛔ A BOUND, BECAUSE THE GUARD CAN OTHERWISE STICK FOREVER — Card P91g item 2.
 *
 * callInFlight is cleared in a `finally` that waits on api.bridgeCRMCall, and
 * that request has NO TIMEOUT of its own. One hung request therefore disables
 * Call on every surface until the window is reloaded — a guard added to stop a
 * double ring, turning into a total outage of the feature, silently.
 *
 * ⚠ 20 s is deliberately longer than a healthy round trip (the handler returns
 * as soon as Twilio accepts the call, not when anyone answers) and shorter than
 * a rep's patience.
 *
 * ⛔⛔ THE TIMEOUT DOES NOT CANCEL THE REQUEST, AND THE COPY SAYS SO. The IPC
 * bridge takes no AbortSignal, so this is Promise.race: a request that comes
 * back at 21 s may STILL have placed a call. That is exactly why the message
 * says "check your phone before trying again" and why NOTHING AUTO-RETRIES —
 * an automatic retry here is the double ring, arriving by a different road.
 */
export const CALL_REQUEST_TIMEOUT_MS = 20000;

/** What a rep is told when the request did not come back in time. */
export const CALL_TIMED_OUT =
  'The call request timed out. Check your phone before trying again.';

/*
 * ⚠ A SHORT COOLDOWN AFTER ACCEPTANCE — Card P91g nit 3.
 *
 * The in-flight guard releases when the API ACCEPTS, which is the moment the
 * rep's phone STARTS RINGING, not the moment the call ends. Without a cooldown
 * a second click a second later places a second call into a phone that is
 * already ringing.
 *
 * ⛔ THIS IS A MITIGATION, NOT A FIX, AND SHOULD NOT BE READ AS ONE. 15 s
 * narrows the window; it does not close it, because a rep who is four minutes
 * into a conversation can still click Call on another contact. Closing it
 * properly means asking the server whether this rep has a live bridge, which
 * is not in this PR.
 */
export const CALL_COOLDOWN_MS = 15000;
let cooldownUntil = 0;

/*
 * ⚠ A MONOTONIC CLOCK, NOT THE WALL CLOCK — Card P89u item 9.
 *
 * `Date.now()` can jump: an NTP correction, a user changing the system time,
 * a laptop resuming from sleep. A backwards jump makes a 15-second cooldown
 * last until the clock catches up; a forwards jump ends it instantly. Neither
 * is a disaster on its own, but the whole point of this guard is "do not ring
 * the rep twice", and a guard that silently stops guarding is worse than one
 * that is absent — nobody thinks to check it. performance.now() counts from an
 * arbitrary origin and never goes backwards.
 *
 * ⚠ Falls back to Date.now() where performance is unavailable. The fallback is
 * the OLD behaviour, so this can only improve matters, never regress them.
 */
function monotonicNow() {
  return (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();
}

/** Start the cooldown. The rep's phone is ringing from this moment. */
function armCooldown() {
  cooldownUntil = monotonicNow() + CALL_COOLDOWN_MS;
}

/*
 * ⛔⛔ THE ORPHANED REQUEST — Card P89v item 1, and the hole the previous
 * round left open.
 *
 * P89u made a LATE acceptance arm the cooldown, which closed the window after
 * the request settles. It did nothing for the window BEFORE it:
 *
 *     t=0s   click, the request hangs
 *     t=20s  we give up, return "timed out", callInFlight is released
 *     t=22s  the rep clicks again — nothing refuses it, a SECOND request goes
 *     t=25s  the first is accepted. The rep's phone rings. Twice.
 *
 * The cooldown could not help: there was nothing to arm it with yet. So a
 * request we stopped waiting for is tracked as PENDING, and Call is refused
 * while it is — the honest position being "we do not know whether your phone
 * is about to ring".
 *
 * ⚠ WITH A HARD CEILING, because the alternative is worse than the bug. A
 * request that never settles would otherwise disable Call for the rest of the
 * session with nothing on screen to explain it — the same permanent-outage
 * failure the 20 s bound itself exists to prevent. Past the ceiling we admit
 * we cannot know and let the rep try.
 */
export const ORPHAN_CEILING_MS = 60000;

/*
 * ⚠ A LIST OF DEADLINES, NOT A COUNT — and the difference is not cosmetic.
 *
 * A count plus one shared ceiling cannot tell a request that never settles
 * from one that just has not settled YET. The count never returns to zero,
 * and the next timeout pushes the shared ceiling forward, so one dead request
 * keeps re-arming the guard behind every later call — mild in production (the
 * ceiling bounds each episode) but unbounded in aggregate, and it leaked
 * straight across test boundaries, where module state outlives the arm that
 * created it.
 *
 * Each orphan carries its OWN deadline and is removed when it settles. One
 * that never settles simply ages out and stops counting, which is exactly
 * what "we can no longer claim to know" should look like.
 */
let orphanDeadlines = [];

/** Is a request we gave up waiting for still unresolved, and still recent? */
function orphanPending() {
  const now = monotonicNow();
  orphanDeadlines = orphanDeadlines.filter((d) => now < d);   // ⚠ prune as we go
  return orphanDeadlines.length > 0;
}

/*
 * ⚠⚠ KNOWN LIMIT: THIS GUARD IS PER WINDOW, NOT PER REP — Card P91g nit 5.
 *
 * It is module state, so it is shared by every surface inside one window and
 * by nothing else. A rep with the CRM open in two tabs, or in the desktop app
 * and a browser at once, has two independent guards and can still ring their
 * own phone twice. Each tab is honest about what it did; neither can see the
 * other.
 *
 * ⛔ The only place that CAN see both is the server, which knows every bridge
 * it has placed for a given user. A real per-rep guard belongs there — a check
 * in crm:calls:bridge for a live bridge row on this user. Deliberately NOT in
 * this PR: it needs its own card, because refusing a call server-side is a
 * behaviour change for a feature Daniel has just restored to the sales team,
 * and the failure mode of getting it wrong is a rep who cannot phone anybody.
 */

/** Is a click-to-call being placed right now, from any surface? */
export function isPlacingCall() {
  return callInFlight;
}

/** Is a second call currently refused — in flight, or inside the cooldown? */
export function isCallRefused() {
  return callInFlight || monotonicNow() < cooldownUntil || orphanPending();
}

/** A value only this module can produce, so a provider cannot forge a timeout. */
const TIMED_OUT = Symbol('click-to-call timed out');

/**
 * Place a bridged click-to-call, with the save-your-number recovery.
 *
 * ⚠ Injectable `api`, `confirm` and `prompt` so a test drives the real flow
 * rather than a re-implementation of it.
 *
 * @returns {Promise<{ok: boolean, message: string, cancelled?: boolean}>}
 */
export async function placeClickToCall(options = {}) {
  /*
   * ⛔ CHECKED FIRST AND HELD ACROSS THE WHOLE FLOW — the confirm, the "enter
   * your callback number" prompt, the save, and both attempts. A rep part-way
   * through typing their number HAS a call in progress; letting another row
   * start one would ring them twice the moment they press OK.
   */
  if (isCallRefused()) {
    // ⚠ An orphan is not "already placing" — see CALL_REQUEST_PENDING.
    const pending = !callInFlight && orphanPending();
    return { ok: false, busy: true, message: pending ? CALL_REQUEST_PENDING : ALREADY_PLACING };
  }
  callInFlight = true;
  try {
    return await runClickToCall(options);
  } finally {
    /*
     * ⚠ finally, never a tail assignment. EVERY early return below — no API, no
     * phone on file, a declined confirm, an empty prompt, a refused save — must
     * release the rep. One cancelled dialog leaking the flag would disable Call
     * everywhere for the rest of the session, with nothing on screen to explain
     * it.
     */
    callInFlight = false;
  }
}

async function runClickToCall({
  contactId,
  contactPhone,
  contactName = '',
  api = (typeof window !== 'undefined' ? window.zignagenixAPI : null),
  confirm = (typeof window !== 'undefined' ? window.confirm.bind(window) : () => true),
  prompt = (typeof window !== 'undefined' ? window.prompt.bind(window) : () => null),
  skipConfirm = false,
} = {}) {
  if (!api || typeof api.bridgeCRMCall !== 'function') {
    return { ok: false, message: 'Calling is not available in this window.' };
  }
  if (!contactPhone) {
    return { ok: false, message: 'No phone number on file for this contact.' };
  }
  if (!skipConfirm) {
    const ok = confirm(
      `Call ${contactName}\nat ${contactPhone}?\n\n`
      + 'Twilio will ring your phone first, then connect you to the contact when you pick up.');
    if (!ok) return { ok: false, cancelled: true, message: '' };
  }

  /*
   * ⚠ clearTimeout on BOTH outcomes. A 20 s timer left armed per attempt keeps
   * the event loop (and, under fake timers, the test) alive for no reason.
   */
  const place = async () => {
    let timer = null;
    const request = api.bridgeCRMCall({ contact_id: contactId, contact_phone: contactPhone });
    try {
      const outcome = await Promise.race([
        request,
        new Promise((resolve) => {
          timer = setTimeout(() => resolve(TIMED_OUT), CALL_REQUEST_TIMEOUT_MS);
        }),
      ]);
      /*
       * ⛔⛔ A TIMEOUT IS NOT A CANCELLATION — Card P89u item 9.
       *
       * Racing the request only stops US waiting; the POST is still on its way
       * to Twilio. If it is accepted a moment after we gave up, the rep's phone
       * rings exactly as if we had waited — but we returned "timed out", armed
       * no cooldown, and the next click rings the same human a second time.
       * That is the precise failure this guard exists to prevent, reached
       * through the one path that skipped it.
       *
       * ⚠ The continuation only ARMS the cooldown. It does not and must not
       * change what the rep was already told: they saw "timed out", and
       * silently flipping that to success later would be a worse lie than the
       * one it corrects.
       */
      if (outcome === TIMED_OUT) {
        /*
         * ⚠ Counted BEFORE the continuation is attached, and released in a
         * `finally` on the request itself — not on this function, which has
         * already returned. Both settle paths release it: an acceptance hands
         * over to the cooldown, a failure means no phone is ringing at all.
         */
        const deadline = monotonicNow() + ORPHAN_CEILING_MS;
        orphanDeadlines.push(deadline);
        const release = () => {
          const i = orphanDeadlines.indexOf(deadline);
          if (i !== -1) orphanDeadlines.splice(i, 1);
        };
        /*
         * ⚠ ONE handler, not .then().catch().finally(). The chained form is
         * three microtask hops, and the release landed a hop or two after the
         * caller had already observed the state — so the guard stayed up past
         * the settle it was waiting for. Both outcomes release here, in the
         * same hop the settle is seen.
         */
        request.then(
          (late) => { if (late && late.success !== false) armCooldown(); release(); },
          () => { release(); },  // ⚠ a late rejection is not a ringing phone
        );
      }
      return outcome;
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  };

  try {
    let r = await place();
    if (r === TIMED_OUT) return { ok: false, timedOut: true, message: CALL_TIMED_OUT };

    if (r && r.success === false && NEEDS_CALLBACK_NUMBER.includes(r.code)) {
      const entered = prompt(
        r.code === 'AGENT_PHONE_NOT_E164'
          ? 'Your saved callback number is not in a format Twilio can ring.\n'
            + 'Re-enter it with the country code, e.g. +16195550123.'
          : 'Enter the number Twilio should ring when you click Call\n'
            + '(e.g. +16195550123). It is saved on your account.');
      if (!entered || !entered.trim()) {
        return { ok: false, message: 'No callback number saved — the call was not placed.' };
      }
      /*
       * ⛔ THE SAVE CAN REFUSE. toE164Strict rejects anything it cannot read
       * unambiguously, and retrying the call after a refused save would run it
       * against the OLD stored value and report whatever THAT failed with — an
       * error about something the rep did not just do.
       */
      const saved = await api.updateOwnProfile({ phone: entered.trim() });
      if (saved && saved.success === false) {
        return { ok: false, message: saved.error || 'That number was not saved.' };
      }
      r = await place();
      if (r === TIMED_OUT) return { ok: false, timedOut: true, message: CALL_TIMED_OUT };
    }

    if (!r || r.success === false) {
      return { ok: false, message: (r && r.error) || 'Click-to-call failed. Check the Twilio settings.' };
    }
    /*
     * ⚠ The cooldown starts HERE — at acceptance, which is when the rep's
     * phone starts ringing. Not on a refusal and not on a timeout: a rep whose
     * call was refused has no ringing phone and must be free to try again.
     */
    armCooldown();
    return { ok: true, message: 'Calling your phone… answer to connect.' };
  } catch (err) {
    return { ok: false, message: `Call failed: ${err && err.message ? err.message : 'unknown error'}` };
  }
}

export default placeClickToCall;
